import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useHistory } from "react-router-dom";
import type { Location } from "history";
import { useAuth } from "../../contexts/UserContext";
import { useSidebar } from "../../contexts/SidebarContext";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import api from "../../services/api";
import toast from "react-hot-toast";
import { toastDefs, toastDefsError } from "../../config/toastConfig";
import { ArrowLeft, DollarSign, HelpCircle, Plus, Save } from "lucide-react";
import AbaHistoricoServico from "../../components/Planos/AbaHistorico/AbaHistoricoServico";
import ModalNovoServico from "../../components/ServicosTemplate/ModalNovoServico";
import ModalConfirmDiscard from "../../components/ModalConfirmDiscard";
import { comum } from "../../styles/Themes/light";
import Tooltip from "../../components/Ui/Tooltip";
import { tituloServicoParaExibicaoPlanos, labelAreaDireitoAudiencia } from "../../utils/servicoMapperPlanos";
import PlanosSkeleton from "../../components/Ui/PlanosSkeleton";
import { PrimaryButton, ActionButton } from "../../components/ListaDemandas/CabecalhoListaDemanda/styles";
import {
  Page,
  HeaderSticky,
  HeaderInner,
  HeaderLeft,
  TabNavSticky,
  TabNavInner,
  TabNav,
  TabButton,
  Content,
  ContentCol,
  Card,
  PricingCardHeader,
  PricingCardHeaderLeft,
  PricingIconWrap,
  PricingTitleWrap,
  PricingTitle,
  HelpIconWrap,
  ButtonGroup,
  TableWrap,
  Table,
  Thead,
  Th,
  Tbody,
  Tr,
  Td,
  TdEmpty,
  TdTooltipTrigger,
  ModalidadeBadge,
  ConsultaBadge,
  LoadingWrap,
  InputSmall,
  TdCellWithError,
  FieldLabel,
  FieldError,
  MobileCardList,
  MobileServiceCard,
  MobileCardEditRow,
  ToggleSwitch,
  ToggleSwitchThumb,
  SobConsultaHeader,
  HelpIconWrapMargin,
} from "./styles";

interface ServicoItem {
  id_servico_template: number;
  servico: string | null;
  area_direito: string | null;
  tipo_audiencia?: string | null;
  modalidade?: string | null;
  profissional?: string | null;
  creditos_padrao: number | null;
  valor_credito: number | null;
  modo_precificacao: string | null;
  servico_ativo: number | null;
  descricao?: string | null;
}

interface DomainData {
  categorias: { id_categoria_de_servico: number; descricao: string }[];
  servicos: { id_servico: number; id_categoria_de_servico: number; titulo: string | null }[];
  tiposAudiencia: { id_tipo_audiencia: number; descricao: string; ordem: number | null }[];
  modalidades: { id_modalidade: number; descricao: string; ordem: number | null }[];
  profissionais: { id_profissional: number; descricao: string; ordem: number | null }[];
}

export default function ServicosTemplate() {
  const history = useHistory();
  const { user, setPageType } = useAuth();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState<ServicoItem[]>([]);
  const [openModalNovoServico, setOpenModalNovoServico] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(false);
  const [draftList, setDraftList] = useState<ServicoItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [showConfirmDiscard, setShowConfirmDiscard] = useState(false);
  const [pendingLocation, setPendingLocation] = useState<Location | null>(null);
  const [templateErrors, setTemplateErrors] = useState<Record<number, { creditos_padrao?: string }>>({});
  const [activeTab, setActiveTab] = useState<"principal" | "historico">("principal");
  const [domainData, setDomainData] = useState<DomainData | null>(null);
  const unblockRef = useRef<(() => void) | null>(null);

  useDocumentTitle({ title: "Template de Serviços" });

  const { isCollapsed: sidebarCollapsed } = useSidebar();
  const isAdmin = user?.profile?.toLowerCase() === "admin";
  const isOperador = user?.profile?.toLowerCase() === "operador";
  const hasUnsavedChanges = Boolean(isAdmin && editingTemplate);

  const fetchTemplate = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/servico-template");
      const data = res.data?.result ?? [];
      setList(Array.isArray(data) ? data : []);
    } catch {
      setList([]);
      toast.error("Erro ao carregar template de serviços.", toastDefsError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAdmin && !isOperador) {
      history.replace("/demandas");
      return;
    }
  }, [isAdmin, isOperador, history]);

  useEffect(() => {
    setPageType("vazio");
    return () => setPageType("default");
  }, [setPageType]);

  useEffect(() => {
    if (isAdmin || isOperador) fetchTemplate();
  }, [fetchTemplate, isAdmin, isOperador]);

  useEffect(() => {
    if (!isAdmin && !isOperador) return;
    Promise.all([
      api.get("/servico-categoria").then((r) => r.data?.result ?? []),
      api.get("/servico").then((r) => r.data?.result ?? []),
      api.get("/servico-tipo-audiencia").then((r) => r.data?.result ?? []),
      api.get("/servico-modalidade").then((r) => r.data?.result ?? []),
      api.get("/servico-profissional").then((r) => r.data?.result ?? []),
    ])
      .then(([categorias, servicos, tiposAudiencia, modalidades, profissionais]) => {
        setDomainData({
          categorias: categorias ?? [],
          servicos: servicos ?? [],
          tiposAudiencia: tiposAudiencia ?? [],
          modalidades: modalidades ?? [],
          profissionais: profissionais ?? [],
        });
      })
      .catch(() => setDomainData(null));
  }, [isAdmin, isOperador]);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const unblock = history.block((nextLoc: Location) => {
      setPendingLocation(nextLoc);
      setShowConfirmDiscard(true);
      return false;
    });
    unblockRef.current = unblock;
    return () => {
      unblockRef.current?.();
      unblockRef.current = null;
    };
  }, [hasUnsavedChanges, history]);

  const handleConfirmDiscard = () => {
    const next = pendingLocation;
    setShowConfirmDiscard(false);
    setPendingLocation(null);
    unblockRef.current?.();
    unblockRef.current = null;
    if (next) history.push(next);
  };

  const handleCloseConfirmDiscard = () => {
    setShowConfirmDiscard(false);
    setPendingLocation(null);
  };

  const handleNovoServico = () => {
    if (!isAdmin) return;
    setOpenModalNovoServico(true);
  };

  const handleEditarTemplate = () => {
    setEditingTemplate(true);
    setDraftList(list.map((s) => ({ ...s })));
  };

  const handleCancelarEdicao = () => {
    setEditingTemplate(false);
    setDraftList([]);
  };

  const handleSalvarTemplate = async () => {
    if (!isAdmin) return;
    const errors: Record<number, { creditos_padrao?: string }> = {};
    for (const row of draftList) {
      if (row.modo_precificacao === "sob_consulta") continue;
      const cred = row.creditos_padrao ?? 0;
      if (cred <= 0) {
        errors[row.id_servico_template] = { creditos_padrao: "Créditos deve ser maior que zero." };
      }
    }
    if (Object.keys(errors).length > 0) {
      setTemplateErrors(errors);
      return;
    }
    setSaving(true);
    setTemplateErrors({});
    try {
      await Promise.all(
        draftList.map((row) =>
          api.patch(`/servico-template/${row.id_servico_template}`, {
            servico_ativo: row.servico_ativo ?? 1,
            modo_precificacao: row.modo_precificacao ?? null,
            creditos_padrao: row.creditos_padrao ?? null,
            valor_credito: row.valor_credito ?? null,
          })
        )
      );
      toast.success("Template atualizado com sucesso.", toastDefs);
      setEditingTemplate(false);
      setDraftList([]);
      fetchTemplate();
    } catch (e: any) {
      toast.error(e.response?.data?.result?.message || e.message || "Erro ao salvar template.", toastDefsError);
    } finally {
      setSaving(false);
    }
  };

  const updateDraft = (id: number, field: keyof ServicoItem, value: string | number | null) => {
    setDraftList((prev) =>
      prev.map((s) =>
        s.id_servico_template === id ? { ...s, [field]: value } : s
      )
    );
    if (field === "creditos_padrao") {
      setTemplateErrors((prev) => {
        const next = { ...prev };
        if (next[id]) {
          delete next[id].creditos_padrao;
          if (Object.keys(next[id]).length === 0) delete next[id];
        }
        return next;
      });
    }
  };

  const toggleSobConsulta = (id: number) => {
    setDraftList((prev) =>
      prev.map((s) =>
        s.id_servico_template === id
          ? {
              ...s,
              modo_precificacao:
                s.modo_precificacao === "sob_consulta" ? "fixo_em_creditos" : "sob_consulta",
            }
          : s
      )
    );
  };

  const handleSuccessNovoServico = () => {
    toast.success("Serviço criado com sucesso.", toastDefs);
    fetchTemplate();
  };

  /** Nomenclatura: Serviço - Área do Direito (Tipo de Audiência caso tenha). API: area_direito=categoria, servico=subtipo. */
  const formatServicoColuna = (
    categoria: string | null | undefined,
    subtipo: string | null | undefined,
    tipoAudiencia?: string | null
  ): string => {
    const s = tituloServicoParaExibicaoPlanos(categoria ?? undefined);
    const a = labelAreaDireitoAudiencia(subtipo ?? undefined) || tituloServicoParaExibicaoPlanos(subtipo ?? undefined);
    const base = [s, a].filter((p) => p != null && String(p).trim() !== "");
    const tipo = tipoAudiencia != null && String(tipoAudiencia).trim() !== "" ? String(tipoAudiencia).trim() : null;
    if (base.length === 0) return "-";
    const principal = base.join(" - ");
    return tipo ? `${principal} (${tipo})` : principal;
  };

  const displayList = useMemo(() => {
    const base = editingTemplate ? draftList : list;
    return [...base].sort((a, b) =>
      formatServicoColuna(a.area_direito, a.servico, a.tipo_audiencia).localeCompare(
        formatServicoColuna(b.area_direito, b.servico, b.tipo_audiencia),
        "pt-BR"
      )
    );
  }, [editingTemplate, draftList, list]);

  if (!isAdmin && !isOperador) return null;

  return (
    <Page>
      <HeaderSticky>
        <HeaderInner>
          <HeaderLeft>
            <div className="voltar-container" onClick={() => history.push("/planos")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && history.push("/planos")} aria-label="Voltar para planos">
              <ArrowLeft size={20} />
              <span>Voltar</span>
            </div>
            <div className="divider" />
            <h1>Template de Serviços</h1>
          </HeaderLeft>
        </HeaderInner>
      </HeaderSticky>

      <TabNavSticky $sidebarCollapsed={sidebarCollapsed}>
        <TabNavInner>
          <TabNav>
            <TabButton type="button" onClick={() => setActiveTab("principal")} $active={activeTab === "principal"}>
              Principal
            </TabButton>
            <TabButton type="button" onClick={() => setActiveTab("historico")} $active={activeTab === "historico"}>
              Histórico
            </TabButton>
          </TabNav>
        </TabNavInner>
      </TabNavSticky>

      <Content $sidebarCollapsed={sidebarCollapsed}>
        <ContentCol>
        {activeTab === "principal" && (
        <>
        {loading ? (
          <LoadingWrap>
            <PlanosSkeleton />
          </LoadingWrap>
        ) : (
          <Card>
            <PricingCardHeader>
              <PricingCardHeaderLeft>
                <PricingIconWrap className="pricing-icon-lg">
                  <DollarSign size={50} color={comum.colors.interfaceGray400} strokeWidth={2} />
                </PricingIconWrap>
                <PricingTitleWrap>
                  <Tooltip content="Precificação de serviços - Template" centerAboveTrigger>
                    <PricingTitle>Precificação de serviços - Template</PricingTitle>
                  </Tooltip>
                  <HelpIconWrap>
                    <Tooltip content="Os serviços do template propagam para todos os planos; alterações afetam apenas novos planos." longContent side="left">
                      <HelpCircle size={16} color={comum.colors.interfaceGray400} />
                    </Tooltip>
                  </HelpIconWrap>
                </PricingTitleWrap>
              </PricingCardHeaderLeft>
              {isAdmin && (
                <ButtonGroup>
                  {editingTemplate ? (
                    <>
                      <ActionButton type="button" onClick={handleCancelarEdicao}>
                        Cancelar
                      </ActionButton>
                      <PrimaryButton type="button" onClick={handleSalvarTemplate} disabled={saving} className="save-btn-labels">
                        <Save size={18} />
                        <span className="btn-label-full">{saving ? "Salvando..." : "Salvar template"}</span>
                        <span className="btn-label-mobile">{saving ? "..." : "Salvar"}</span>
                      </PrimaryButton>
                    </>
                  ) : (
                    <>
                      <ActionButton type="button" onClick={handleEditarTemplate}>
                      Editar serviços
                      </ActionButton>
                      <PrimaryButton type="button" onClick={handleNovoServico}>
                        <Plus size={18} /> Novo serviço
                      </PrimaryButton>
                    </>
                  )}
                </ButtonGroup>
              )}
            </PricingCardHeader>

            <TableWrap>
              <MobileCardList>
                {displayList.map((s) => {
                  const sobConsulta = s.modo_precificacao === "sob_consulta";
                  return (
                    <MobileServiceCard key={s.id_servico_template}>
                      <div className="mobile-card-header">
                        <span className="mobile-card-servico" title={formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)}>
                          <Tooltip content={formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)} longContent centerAboveTrigger>
                            <span className="mobile-card-servico-trigger">
                              {formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)}
                            </span>
                          </Tooltip>
                        </span>
                        <span className="mobile-card-creditos-header">
                          {sobConsulta ? (
                            <ConsultaBadge>Consulta</ConsultaBadge>
                          ) : (
                            s.creditos_padrao != null ? `${s.creditos_padrao} créditos` : "- créditos"
                          )}
                        </span>
                      </div>
                      <div className="mobile-card-tag-profissional">
                        {s.modalidade ? <ModalidadeBadge>{s.modalidade}</ModalidadeBadge> : null}
                        <span className="mobile-card-profissional-value">
                          {s.profissional ?? "-"}
                        </span>
                      </div>
                      {editingTemplate && (
                        <MobileCardEditRow>
                          <div className="mobile-edit-creditos-row">
                            <FieldLabel className="no-margin">Créditos:</FieldLabel>
                            {sobConsulta ? (
                              <span className="mobile-card-profissional-value">-</span>
                            ) : (
                              <TdCellWithError $hasError={Boolean(templateErrors[s.id_servico_template]?.creditos_padrao)}>
                                <InputSmall
                                  type="number"
                                  min={0}
                                  value={s.creditos_padrao ?? ""}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === "") {
                                      updateDraft(s.id_servico_template, "creditos_padrao", null);
                                      return;
                                    }
                                    const num = parseInt(raw, 10);
                                    if (!Number.isNaN(num) && num >= 0) {
                                      updateDraft(s.id_servico_template, "creditos_padrao", num);
                                    }
                                  }}
                                  placeholder="-"
                                />
                                {templateErrors[s.id_servico_template]?.creditos_padrao ? (
                                  <FieldError>{templateErrors[s.id_servico_template].creditos_padrao}</FieldError>
                                ) : null}
                              </TdCellWithError>
                            )}
                          </div>
                          <div className="mobile-edit-sobconsulta-row">
                            <span className="label">Sob consulta</span>
                            <ToggleSwitch
                              type="button"
                              role="switch"
                              aria-checked={sobConsulta}
                              $on={sobConsulta}
                              onClick={() => toggleSobConsulta(s.id_servico_template)}
                            >
                              <ToggleSwitchThumb $on={sobConsulta} />
                            </ToggleSwitch>
                          </div>
                        </MobileCardEditRow>
                      )}
                    </MobileServiceCard>
                  );
                })}
              </MobileCardList>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Serviço</Th>
                    <Th className="center">Modalidade</Th>
                    <Th>Profissional</Th>
                    <Th className="center">Créditos</Th>
                    {editingTemplate && (
                      <Th className="right">
                        <SobConsultaHeader>
                          Sob Consulta
                          <HelpIconWrapMargin as="span">
                            <Tooltip content="Quando ativado, o serviço fica marcado como Sob Consulta: não exige créditos nem valor fixo; o valor é definido sob consulta." side="left" longContent>
                              <HelpCircle size={12} color={comum.colors.interfaceGray400} aria-label="Ajuda sobre Sob Consulta" />
                            </Tooltip>
                          </HelpIconWrapMargin>
                        </SobConsultaHeader>
                      </Th>
                    )}
                  </Tr>
                </Thead>
                <Tbody>
                  {displayList.length === 0 ? (
                    <Tr>
                      <TdEmpty colSpan={editingTemplate ? 5 : 4}>
                        Nenhum serviço no template. Clique em Novo preço para adicionar.
                      </TdEmpty>
                    </Tr>
                  ) : (
                    displayList.map((s) => {
                      const sobConsulta = s.modo_precificacao === "sob_consulta";
                      return (
                        <Tr key={s.id_servico_template}>
                          <Td>
                            {s.descricao?.trim() ? (
                              <Tooltip content={s.descricao.trim()} longContent centerAboveTrigger alignAbove="start">
                                <TdTooltipTrigger>
                                  {formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)}
                                </TdTooltipTrigger>
                              </Tooltip>
                            ) : (
                              formatServicoColuna(s.area_direito, s.servico, s.tipo_audiencia)
                            )}
                          </Td>
                          <Td className="center">
                            {s.modalidade ? <ModalidadeBadge>{s.modalidade}</ModalidadeBadge> : "-"}
                          </Td>
                          <Td>{s.profissional ?? "-"}</Td>
                          <Td className="center">
                            {editingTemplate ? (
                              sobConsulta ? (
                                "-"
                              ) : (
                                <TdCellWithError $hasError={Boolean(templateErrors[s.id_servico_template]?.creditos_padrao)}>
                                  <InputSmall
                                    type="number"
                                    min={0}
                                    value={s.creditos_padrao ?? ""}
                                    onChange={(e) => {
                                      const raw = e.target.value;
                                      if (raw === "") {
                                        updateDraft(s.id_servico_template, "creditos_padrao", null);
                                        return;
                                      }
                                      const num = parseInt(raw, 10);
                                      if (Number.isNaN(num) || num < 0) return;
                                      updateDraft(s.id_servico_template, "creditos_padrao", num);
                                    }}
                                    placeholder="-"
                                  />
                                  {templateErrors[s.id_servico_template]?.creditos_padrao ? (
                                    <FieldError>{templateErrors[s.id_servico_template].creditos_padrao}</FieldError>
                                  ) : null}
                                </TdCellWithError>
                              )
                            ) : sobConsulta ? (
                              <ConsultaBadge>Consulta</ConsultaBadge>
                            ) : (
                              s.creditos_padrao != null ? s.creditos_padrao : "-"
                            )}
                          </Td>
                          {editingTemplate && (
                            <Td className="right">
                              <ToggleSwitch
                                type="button"
                                role="switch"
                                aria-checked={sobConsulta}
                                $on={sobConsulta}
                                onClick={() => toggleSobConsulta(s.id_servico_template)}
                              >
                                <ToggleSwitchThumb $on={sobConsulta} />
                              </ToggleSwitch>
                            </Td>
                          )}
                        </Tr>
                      );
                    })
                  )}
                </Tbody>
              </Table>
            </TableWrap>
          </Card>
        )}
        </>
        )}

        {activeTab === "historico" && (
          <AbaHistoricoServico abaAtiva={activeTab} loading={loading} />
        )}
        </ContentCol>
      </Content>

      <ModalNovoServico
        open={openModalNovoServico}
        onClose={() => setOpenModalNovoServico(false)}
        onSuccess={handleSuccessNovoServico}
        domainData={domainData}
      />

      <ModalConfirmDiscard
        open={showConfirmDiscard}
        onClose={handleCloseConfirmDiscard}
        onDiscard={handleConfirmDiscard}
      />
    </Page>
  );
}
